import * as React from "react";
import { Avatar, Message } from "@chatscope/chat-ui-kit-react";
import { KAHUN_AVATAR_SRC } from "../styles/Theme";
import {
  AnswerFooter,
  FooterHandle,
} from "fe-web-components/references/AnswerFooter";
import { safeCopyToClipboard } from "../utils/safeCopyToClipboard";
import {
  ChatMessage,
  GuidelineData,
  MessageType,
} from "../context/sessionStore";
import { CitationMap } from "fe-web-components/references/types";
import useSessionStore from "../context/useSessionStore";
import { SuggestedQuestion } from "./SuggestedQuestion";
import { useCallback, useEffect, useRef, useState } from "react";
import { ExchangeFeedback, PredefinedInsight } from "../graphql/types/graphql";
import { ThreeDotsLoader } from "./UI/ThreeDotsLoader";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { GuidelineIndicator } from "fe-web-components/guidelines/GuidelineIndicator";
import { isRtl } from "../utils";
import { FeedbackInput } from "fe-web-components/feedback/types";
import { addAnnotations } from "fe-web-components/references/utils";
import { AnnotatedText } from "fe-web-components/references/AnnotatedText";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface AnswerProps {
  message: ChatMessage;
  streaming: boolean;
  debugMode: boolean;
  getGuideline:
    | ((index: number, guidelineId: string) => Promise<ChatMessage>)
    | null;
  feedback?: ExchangeFeedback;
  historic?: boolean;
  question?: string;
  standalone?: boolean; //answer is display outside of chat
  usedPrompt?: {
    promptName: string;
    predefinedInsight: PredefinedInsight;
  };
}

//endregion [[ Props ]]

//region [[ Functions ]]
// TODO should be done in the server
function stripIDsFromStreamingText(str: string) {
  let newStr = str;
  const pattern = /(?:\(source:)?\(? ?ID\.[0-9]+,?\)?/gm;

  for (let match of str.matchAll(pattern)) {
    newStr = newStr.replace(match[0], "");
  }
  return newStr;
}
function stripFootnotes(str: string) {
  let newStr = str;
  const pattern = /\^\d+/gm;

  for (let match of str.matchAll(pattern)) {
    newStr = newStr.replace(match[0], "");
  }
  return newStr;
}

//endregion [[ Functions ]]

export const Answer = ({
  message,
  debugMode,
  streaming,
  historic,
  standalone,
  getGuideline,
  usedPrompt,
}: AnswerProps) => {
  const {
    index,
    text,
    questions,
    footNotes,
    feedback,
    type,
    guidelines,
    citations: messageCitations,
  } = message;
  const [answerText, setAnswerText] = useState<string>(text);
  const [citations, setCitations] = useState<CitationMap>(message.citations);
  const [updatingAnswer, setUpdatingAnswer] = useState<boolean>(false);
  const [guidelinesCache, setGuidelinesCache] = useState<GuidelineData[]>([]);
  const { ragSettings, sendFeedback } = useSessionStore();
  const rtlDisplay = isRtl(answerText, ragSettings?.rtlDisplay);
  const reporter = useEventReportedContext();
  const footerRef = useRef<FooterHandle>(null);
  const guidelinesCached = useRef(false);

  const handleSendFeedback = useCallback(
    async (exchangeIndex: number, feedbackInput: FeedbackInput) => {
      const success = await sendFeedback(
        feedbackInput,
        exchangeIndex,
        usedPrompt?.promptName,
        usedPrompt?.predefinedInsight,
      );
      if (success)
        reporter?.reportEvent(MIX_PANEL_EVENTS.FEEDBACK, feedbackInput);
      return success;
    },
    [reporter, sendFeedback, usedPrompt],
  );

  const onGuideLineChange = useCallback(
    async (fromId: string, toId: string) => {
      setAnswerText("");
      reporter?.reportEvent(MIX_PANEL_EVENTS.GUIDELINE_CHANGED, {
        from_guideline: fromId,
        to_guideline: toId,
      });
      const cachedData = guidelinesCache.find((g) => g.guideline.id === toId)
        ?.data;

      if (cachedData) {
        setAnswerText(
          addAnnotations(cachedData.text, cachedData.footNotes, debugMode),
        );
        setCitations(cachedData.citations);
      } else if (getGuideline) {
        setUpdatingAnswer(true);
        getGuideline(index, toId)
          .then((guidelineMessage) => {
            if (guidelineMessage) {
              setGuidelinesCache((prev) => {
                //update the cache
                const updatedCache = [...prev];
                const cachedGuideline = updatedCache.find(
                  (g) => g.guideline.id === toId,
                );
                if (cachedGuideline) cachedGuideline.data = guidelineMessage;
                return updatedCache;
              });
              setAnswerText(
                addAnnotations(
                  guidelineMessage.text,
                  guidelineMessage.footNotes,
                  debugMode,
                ),
              );
              setCitations(guidelineMessage.citations);
            }
            setUpdatingAnswer(false);
          })
          .catch(() => {
            setUpdatingAnswer(false);
          });
      }
    },
    [guidelinesCache, index, debugMode, reporter, getGuideline],
  );

  useEffect(() => {
    if (!guidelines || guidelines?.length === 0) return;
    //create the initial cache
    if (!guidelinesCached.current) {
      //store the list of guidelines
      setGuidelinesCache(
        guidelines.map(({ __typename, ...g }) => ({
          guideline: g,
          data: null,
        })),
      );
      guidelinesCached.current = true;
    } else if (
      !streaming &&
      guidelinesCache.length > 0 &&
      !guidelinesCache[0].data
    ) {
      setGuidelinesCache((prevState) => {
        const cache = [...prevState];
        cache[0].data = message; //store the first guideline data after streaming finished
        return cache;
      });
    }
  }, [streaming, message, guidelines]);

  useEffect(() => {
    setAnswerText(
      streaming
        ? stripIDsFromStreamingText(text)
        : addAnnotations(text, footNotes, debugMode),
    );
    setCitations(messageCitations);
  }, [text, streaming, footNotes, debugMode, messageCitations]);

  return (
    <Message
      model={{
        payload: (
          <Message.CustomContent>
            <div ref={message.ref} dir={rtlDisplay ? "rtl" : ""}>
              {guidelinesCache?.length > 0 && (
                <GuidelineIndicator
                  guidelines={guidelinesCache.map((g) => {
                    return g.guideline;
                  })}
                  onChange={onGuideLineChange}
                  disabled={historic || streaming} //disable guideline change
                  showIgnoreButton={!standalone}
                />
              )}
              {answerText ? (
                <>
                  <AnnotatedText
                    markdown={answerText}
                    onFootnoteClick={(id) => {
                      footerRef.current?.highlightRef(id);
                    }}
                  />
                  {streaming && <ThreeDotsLoader small />}
                </>
              ) : (
                <ThreeDotsLoader />
              )}
            </div>
          </Message.CustomContent>
        ),
        sentTime: "just now",
        sender: "Kahun",
        direction: "incoming",
        position: "normal",
        type: "html",
      }}
      avatarPosition={rtlDisplay ? "tr" : "tl"}
    >
      {!standalone && (
        <Avatar
          src={KAHUN_AVATAR_SRC}
          name="Kahun"
          size={historic ? "sm" : "md"}
        />
      )}
      {!streaming && !updatingAnswer && (
        <Message.Footer>
          {type === MessageType.suggestedQuestions ? (
            questions && <SuggestedQuestion questions={questions} />
          ) : (
            <AnswerFooter
              citations={citations}
              debugMode={debugMode}
              onCopy={() => safeCopyToClipboard(stripFootnotes(answerText))}
              onToggleRefs={(open: boolean) => {
                if (!open) reporter?.reportEvent(MIX_PANEL_EVENTS.REF_OPENED);
              }}
              rtlDisplay={rtlDisplay}
              feedback={feedback}
              onSendFeedback={(feedback) => handleSendFeedback(index, feedback)}
              ref={footerRef}
            />
          )}
        </Message.Footer>
      )}
    </Message>
  );
};
